import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable } from '@angular/core';
import {
  injectMutation,
  injectQuery,
  QueryClient,
} from '@tanstack/angular-query-experimental';
import { lastValueFrom, take } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AuthSessionService } from '../../auth/services/auth-session.service';
import { SubdomainService } from '../../../core/services/subdomain.service';
import { isIdentityLayerDisabledError } from '../helpers/identity-error.helper';
import {
  canExpandFamily,
  shouldPromptIdentityVerification,
} from '../helpers/identity-policy.helper';
import {
  IdentityLayerConfig,
  IdentityVerificationStatus,
  IdentityVerificationTier,
  IDENTITY_CONSENT_TERMS_VERSION,
  isDocumentVerifiedTier,
  KycConsentRequest,
  KycSession,
  KycSimulateRequest,
  needsIdentityVerification,
} from '../models/identity-verification.model';

@Injectable({ providedIn: 'root' })
export class ParentIdentityService {
  #http = inject(HttpClient);
  #session = inject(AuthSessionService);
  #subdomain = inject(SubdomainService);
  #qc = inject(QueryClient);

  readonly configQuery = injectQuery(() => {
    const shouldLoad = this.#shouldLoadIdentityConfig();

    return {
      queryKey: ['identity', 'config'],
      queryFn: () => this.fetchConfig(),
      staleTime: 30_000,
      enabled: shouldLoad,
    };
  });

  readonly statusQuery = injectQuery(() => {
    const shouldLoad = this.#shouldLoadIdentityConfig();
    const layerEnabled = Boolean(this.configQuery.data()?.layerEnabled);

    return {
      queryKey: ['identity', 'status'],
      queryFn: () => this.fetchStatus(),
      enabled: shouldLoad && layerEnabled,
      retry: 1,
    };
  });

  readonly sessionQuery = injectQuery(() => ({
    queryKey: ['identity', 'kyc-session'],
    queryFn: () => this.fetchSession(),
    enabled: false,
    retry: false,
  }));

  readonly createSessionMutation = injectMutation(() => ({
    mutationFn: (dto: KycConsentRequest) =>
      lastValueFrom(
        this.#http
          .post<KycSession>(`${environment.baseUrl}/user/identity/kyc/session`, {
            consentAccepted: dto.consentAccepted,
            termsVersion: dto.termsVersion,
            password: dto.password ?? '',
            totpCode: dto.totpCode ?? '',
          })
          .pipe(take(1)),
      ),
    onSuccess: (session) => {
      this.#qc.setQueryData<KycSession>(['identity', 'kyc-session'], session);
      const status = this.statusQuery.data();
      if (status) {
        this.#qc.setQueryData<IdentityVerificationStatus>(['identity', 'status'], {
          ...status,
          tier: IdentityVerificationTier.KYC_PENDING,
          activeKycSessionId: session.id,
          kycProvider: session.provider,
        });
      }
      void this.#qc.invalidateQueries({ queryKey: ['identity', 'status'] });
    },
  }));

  readonly simulateMutation = injectMutation(() => ({
    mutationFn: (dto: KycSimulateRequest) =>
      lastValueFrom(
        this.#http
          .post<IdentityVerificationStatus>(
            `${environment.baseUrl}/user/identity/kyc/simulate`,
            {
              success: dto.success !== false,
              documentCountry: dto.documentCountry ?? '',
              birthDate: dto.birthDate ?? '',
            },
          )
          .pipe(take(1)),
      ),
    onSuccess: (status) => {
      this.#qc.setQueryData(['identity', 'status'], status);
    },
  }));

  readonly layerEnabled = computed(() =>
    Boolean(this.configQuery.data()?.layerEnabled),
  );
  readonly configReady = computed(() => this.configQuery.isFetched());
  readonly status = computed(() => this.statusQuery.data() ?? null);
  readonly session = computed(() => this.sessionQuery.data() ?? null);
  readonly loaded = computed(
    () =>
      !this.layerEnabled() ||
      this.statusQuery.isFetched() ||
      this.statusQuery.isError(),
  );
  readonly inGracePeriod = computed(() => Boolean(this.status()?.inGracePeriod));
  readonly graceDaysRemaining = computed(
    () => this.status()?.graceDaysRemaining ?? 0,
  );
  readonly shouldPromptVerification = computed(() =>
    shouldPromptIdentityVerification(this.status(), this.layerEnabled()),
  );
  readonly canExpandFamily = computed(() =>
    canExpandFamily(this.status(), this.layerEnabled()),
  );
  readonly isFullyVerified = computed(
    () => this.status()?.tier === IdentityVerificationTier.FULLY_VERIFIED,
  );

  isLayerEnabled(): boolean {
    return this.layerEnabled();
  }

  isDocumentVerified(): boolean {
    return isDocumentVerifiedTier(this.status()?.tier);
  }

  needsVerification(): boolean {
    return needsIdentityVerification(this.status()?.tier);
  }

  async whenConfigReady(): Promise<void> {
    if (!this.#shouldLoadIdentityConfig()) {
      return;
    }

    if (!this.configQuery.isFetched()) {
      await this.configQuery.refetch();
    }

    if (this.layerEnabled() && !this.statusQuery.isFetched()) {
      await this.statusQuery.refetch();
    }
  }

  /**
   * Load identity config as soon as the family session exists.
   * Do not wait to leave `/auth` — post-login navigation still has that URL when
   * `navigateAfterAuth` / header bootstrap call `whenConfigReady()`.
   */
  #shouldLoadIdentityConfig(): boolean {
    return (
      this.#session.isAuthenticated() && this.#subdomain.isFamilySubdomain()
    );
  }

  async reload(): Promise<IdentityVerificationStatus | null> {
    if (!this.layerEnabled()) {
      return null;
    }
    const result = await this.statusQuery.refetch();
    return result.data ?? null;
  }

  async loadSession(): Promise<KycSession | null> {
    try {
      const session = await this.#qc.fetchQuery({
        queryKey: ['identity', 'kyc-session'],
        queryFn: () => this.fetchSession(),
      });
      return session;
    } catch {
      this.#qc.setQueryData(['identity', 'kyc-session'], null);
      return null;
    }
  }

  async createSession(consent?: KycConsentRequest): Promise<KycSession> {
    return this.createSessionMutation.mutateAsync(
      consent ?? {
        consentAccepted: true,
        termsVersion: IDENTITY_CONSENT_TERMS_VERSION,
      },
    );
  }

  async simulateVerification(
    dto: KycSimulateRequest,
  ): Promise<IdentityVerificationStatus> {
    return this.simulateMutation.mutateAsync(dto);
  }

  private async fetchConfig(): Promise<IdentityLayerConfig> {
    try {
      return await lastValueFrom(
        this.#http
          .get<IdentityLayerConfig>(
            `${environment.baseUrl}/user/identity/config`,
          )
          .pipe(take(1)),
      );
    } catch {
      return { layerEnabled: false };
    }
  }

  private async fetchStatus(): Promise<IdentityVerificationStatus | null> {
    try {
      return await lastValueFrom(
        this.#http
          .get<IdentityVerificationStatus>(
            `${environment.baseUrl}/user/identity`,
          )
          .pipe(take(1)),
      );
    } catch (error) {
      if (isIdentityLayerDisabledError(error)) {
        return null;
      }
      throw error;
    }
  }

  private async fetchSession(): Promise<KycSession | null> {
    try {
      return await lastValueFrom(
        this.#http
          .get<KycSession>(`${environment.baseUrl}/user/identity/kyc/session`)
          .pipe(take(1)),
      );
    } catch {
      return null;
    }
  }
}
